/**
 * Action-related constants for case record actions
 */

// Map action values to their display labels
export const ACTION_LABELS: Record<string, string> = {
  call_with_ai: 'Send AI Call',
  call_with_human: 'Human Call',
  mark_as_completed: 'Mark case as complete',
  mark_as_terminated: 'Mark as Terminated',
  retry_call: 'Retry Call',
  human_call_mark_as_completed: 'Mark Human Call as Completed',
  human_call_mark_as_terminated: 'Mark Human Call as Terminated',
  human_call_mark_as_failed: 'Mark Human Call as Failed',
  // Transportation and unifiedmedbv (BV-2514, BV-2657): a Program Admin moves a human
  // call to human audit.
  human_call_move_to_human_audit: 'Move to Human Audit',
  cancel_case: 'Cancel Case',
  cancel_workflow: 'Cancel Workflow',
  cancel_call: 'Cancel Call',
  // Refill-only (JNJ-109): ends a parked case's scheduled retry attempt. The
  // dropdown label normally comes from the workflow document's status config;
  // this is the fallback used by confirmation copy and toasts.
  refill_cancel_scheduled_call: 'Cancel Scheduled Call',
  // Refill-only (JNJ-172): release a case parked in human_audit_needed.
  refill_human_audit_retry: 'Retry',
  refill_human_audit_skip: 'Continue',
  // Refill-only: complete a case parked in human_call_needed.
  refill_mark_case_as_completed: 'Mark Case as Completed',
  cancel_ai_audit: 'Cancel AI Audit',
  // Start a human-audit session (BI-1246)
  start_human_audit: 'Start Human Audit',
  mark_as_duplicate: 'Mark as Duplicate',
  // Eligibility check human audit actions
  eligibility_check_retry: 'Retry Eligibility Check',
  eligibility_check_proceed: 'Proceed to Human Audit',
  submit_for_review: 'Submit for review',
  submit_to_manufacturer: 'Submit to manufacturer',
  // PQC mark as completed action (when ack_id received from manufacturer)
  pqc_mark_as_completed: 'Mark as Completed',
  // PQC manual output update (routes through hermes + PQCAgent)
  pqc_update_output: 'Update Output',
  // PQC attach to case action (to attach a specific call to the case)
  attach_to_case: 'attach this call to the case',
  // Medicare Human direct completion action
  mark_case_completed: 'Mark Case as Completed',
  // Recall Scheduler update reachout note action
  update_note: 'Update Reachout-note',
  // UnifiedMedBV EC Human Audit actions
  reinitiate_ec: 'Reinitiate Eligibility Check',
  move_to_ai_call: 'Move to AI Call',
  move_to_human_call: 'Move to Human Call',
  move_to_medicare: 'Move to Medicare Coverage',
  complete: 'Complete',
  terminate: 'Terminate',
  // UnifiedMedBV Human Audit actions
  reinitiate_ai_call: 'Reinitiate AI Call',
  audit_move_to_human_call: 'Move to Human Call',
  audit_move_to_medicare: 'Move to Medicare Coverage',
};

// Map of action-specific confirmation messages
export const ACTION_CONFIRMATION_MESSAGES: Record<string, string> = {
  call_with_ai: 'Are you sure you want to send an AI call?',
  call_with_human: 'Are you sure you want to initiate a human call?',
  mark_as_terminated: 'Are you sure you want to mark this case as terminated?',
  cancel_case:
    'Are you sure you want to cancel this case? This action cannot be undone and will terminate the current case.',
  cancel_workflow:
    'Are you sure you want to cancel this case? This action cannot be undone and will terminate the current case.',
  cancel_call: 'Are you sure you want to end the current call? This action cannot be undone.',
  cancel_ai_audit:
    'Are you sure you want to cancel the AI audit? The case will move to human audit.',
  refill_human_audit_retry:
    'Retry the step that failed? Any input or output fixes you saved are used. If the cause persists, the case will come back here.',
  refill_human_audit_skip:
    'Skip the step that failed? If it was the CareTend lookup, the case continues to screening and the call WITHOUT the CareTend data. If it was the delivery ticket, the case is COMPLETED WITHOUT creating a ticket, so nothing will be shipped. This cannot be undone.',
  refill_mark_case_as_completed:
    'Mark this case as completed? Only do this once someone has called the patient and resolved what is listed on the case. This cannot be undone.',
  mark_as_duplicate:
    'Are you sure you want to mark this case as a duplicate? This action cannot be undone.',
  eligibility_check_retry:
    'Are you sure you want to retry the eligibility check? This will restart the eligibility verification process.',
  eligibility_check_proceed: 'Are you sure you want to proceed? This action cannot be undone.',
  pqc_mark_as_completed:
    'Are you sure you want to mark this case as completed? This will finalize the case with the acknowledgement from the manufacturer.',
  mark_case_completed:
    'Are you sure you want to mark this case as completed? This will bypass the secondary phase and directly complete the case.',
  // UnifiedMedBV EC Human Audit actions
  reinitiate_ec: 'Are you sure you want to reinitiate the eligibility check?',
  move_to_ai_call: 'Are you sure you want to move this case to AI call?',
  move_to_human_call: 'Are you sure you want to move this case to human call?',
  move_to_medicare: 'Are you sure you want to move this case to Medicare coverage?',
  complete: 'Are you sure you want to complete this case?',
  terminate: 'Are you sure you want to terminate this case?',
  // UnifiedMedBV Human Audit actions
  reinitiate_ai_call: 'Are you sure you want to reinitiate the AI call?',
  audit_move_to_human_call: 'Are you sure you want to move this case to human call?',
  audit_move_to_medicare: 'Are you sure you want to move this case to Medicare coverage?',
};

/**
 * Actions that require disposition to be set before confirmation.
 * Used for PBM and UnifiedMedBV workflows to enforce disposition selection.
 */
export const DISPOSITION_REQUIRED_ACTIONS = [
  'mark_as_completed',
  'mark_as_terminated',
  'complete', // UnifiedMedBV EC Human Audit
  'terminate', // UnifiedMedBV EC Human Audit
  // Finishing a human call. The dialpad's post-call form used to require a
  // disposition before its Save; that form is gone, so this dialog is now the
  // only place the requirement can be enforced.
  'human_call_mark_as_completed',
  'human_call_mark_as_terminated',
  // The transportation and unifiedmedbv Program Admin's move ends the human-call step
  // too, and the server gates it on the same disposition (BV-2514, BV-2657).
  'human_call_move_to_human_audit',
];

/**
 * Actions that end the human-call step the way a completion does: the server gates
 * them on the case's latest human call carrying a disposition, and the Confirm dialog
 * writes a disposition picked there onto that call (BI-1848). The transportation and
 * unifiedmedbv Program Admin's Move to Human Audit (BV-2514, BV-2657) is one.
 */
export const HUMAN_CALL_COMPLETION_ACTIONS = [
  'human_call_mark_as_completed',
  'human_call_move_to_human_audit',
];

/**
 * Action category configuration with order preserved (Map maintains insertion order)
 * The key is used as both the category identifier and the display label
 */
export const ACTION_CATEGORIES = new Map<string, string[]>([
  [
    'Proceed',
    [
      'start_human_audit',
      'retry_call',
      'human_call_mark_as_completed',
      'human_call_move_to_human_audit',
      'human_call_mark_as_failed',
      'eligibility_check_retry',
      'eligibility_check_proceed',
      'mark_case_completed',
    ],
  ],
  [
    '',
    ['submit_for_review', 'submit_to_manufacturer', 'pqc_mark_as_completed', 'pqc_resend_email'],
  ],
  ['Call Actions', ['cancel_call', 'refill_cancel_scheduled_call']],
  // Human-audit grouping (pbm / unifiedmedbv / transportation): "Call again"
  // (human before AI). "Close case" always renders last (see buildCategorizedResult).
  [
    'Call again',
    [
      'call_with_human', // pbm / transportation: "Call with Human"
      'call_with_ai', // pbm / transportation: "Call with AI"
      'audit_move_to_human_call', // unifiedmedbv: "Route to Human Call"
      'reinitiate_ai_call', // unifiedmedbv: "Retry AI Call"
    ],
  ],
  ['Human Audit', ['refill_human_audit_retry', 'refill_human_audit_skip']],
  ['Close case', ['mark_as_completed', 'mark_as_terminated', 'refill_mark_case_as_completed']],
]);
